import { environment } from '@app/store/envStore.svelte.js';
import { authState } from '@app/store/authStore.svelte.js';
import { toast } from '@zerodevx/svelte-toast';
import { auth } from "@app/configs/firebase.js";
import db from "@app/configs/firestore.js";
import { collection, doc, query, where, getDoc, getDocs, setDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { v4 as uuidv4 } from 'uuid';
import _Tag from "@cclib/classes/tag.js";

const SELECTED_TAG_SET_STORAGE_KEY = "cc-selected-tag-set-id";

// ── Helpers ──────────────────────────────────────────────────────────

export function normalizeTagSetId(tagSetId) {
    if (tagSetId === null || tagSetId === undefined) {
        return null;
    }
    return String(tagSetId);
}

function getSavedSelectedTagSetId() {
    try {
        return normalizeTagSetId(localStorage.getItem(SELECTED_TAG_SET_STORAGE_KEY));
    } catch {
        return null;
    }
}

export function saveSelectedTagSetId(selectedTagSetId) {
    try {
        const normalized = normalizeTagSetId(selectedTagSetId);
        if (normalized) {
            localStorage.setItem(SELECTED_TAG_SET_STORAGE_KEY, normalized);
            return;
        }
        localStorage.removeItem(SELECTED_TAG_SET_STORAGE_KEY);
    } catch { /* ignore */ }
}

// ── Firebase refs (resolved lazily once per session) ─────────────────

function getFirebaseRefs() {
    if (!environment.online) return { homeRef: null, userRef: null };
    const userId = auth.currentUser.uid;
    const teamId = authState.team ? authState.team.id : null;
    const homeRef = teamId
        ? collection(db, "teams", teamId, "tags")
        : collection(db, "users", userId, "tags");
    const userRef = doc(db, "users", userId);
    return { homeRef, userRef };
}

// ── Reactive state (module-level singleton) ──────────────────────────

export const tagState = $state({
    /** Active tag sets shown in the Tags panel dropdown */
    sets: [],
    /** IDs of active (enabled) tag sets */
    activeSets: [],
    /** All tag sets (used by TagsManager for CRUD) */
    allSets: [],
    /** Currently selected tag set id in the Tags panel */
    selectedId: null,
    /** Whether the initial load has completed */
    loaded: false,
    /** Whether a load is in progress */
    loading: false,
});

/** The tag set object that matches `tagState.selectedId` */
const selectedTagSet = $derived(
    tagState.sets.find((set) => {
        return normalizeTagSetId(set.id) === normalizeTagSetId(tagState.selectedId);
    }) || null
);

export function getSelectedTagSet() {
    return selectedTagSet;
}

// ── Tag color mapping ────────────────────────────────────────────────

export const tagColorMapping = {
    Character: "info",
    Location: "light",
    Organization: "warning",
    Phrase: "success",
    Other: "secondary"
};

// ── Selection logic ──────────────────────────────────────────────────

/**
 * Set `tagState.selectedId`, preferring the given id, then localStorage,
 * then falling back to the first available set.
 */
export function setSelectedTagSet(preferredSelectedId = null) {
    const target = normalizeTagSetId(preferredSelectedId) || getSavedSelectedTagSetId();
    const match = tagState.sets.find((set) => normalizeTagSetId(set.id) === target);

    if (match) {
        tagState.selectedId = normalizeTagSetId(match.id);
    } else if (tagState.sets.length > 0) {
        tagState.selectedId = normalizeTagSetId(tagState.sets[0].id);
    } else {
        tagState.selectedId = null;
    }

    // Persist immediately
    saveSelectedTagSetId(tagState.selectedId);
}

// ── Data loading: active sets (Tags panel) ───────────────────────────

/**
 * Load the active (enabled) tag sets for the Tags panel.
 * If data is already loaded and `force` is false, resolves immediately.
 */
export async function loadTagSets(preferredSelectedId = null, force = false) {
    if (tagState.loaded && !force) {
        setSelectedTagSet(preferredSelectedId);
        return;
    }

    tagState.loading = true;

    try {
        if (environment.online) {
            const { homeRef, userRef } = getFirebaseRefs();
            const userDoc = await getDoc(userRef);
            const userInfo = userDoc.data();
            const activeIds = userInfo.tags || [];

            tagState.activeSets = activeIds;

            if (activeIds.length > 0) {
                const querySnapshot = await getDocs(query(homeRef, where("id", "in", activeIds)));
                const sets = [];
                querySnapshot.forEach((tagSetDoc) => sets.push(tagSetDoc.data()));
                tagState.sets = sets;
            } else {
                tagState.sets = [];
            }
        } else {
            const savedActiveSets = JSON.parse(localStorage.getItem("cc-active-tag-sets")) || [];
            const savedTagSets = JSON.parse(localStorage.getItem("cc-tag-sets")) || [];

            tagState.activeSets = savedActiveSets;
            tagState.sets = savedTagSets.filter((set) => savedActiveSets.includes(set.id));
        }

        setSelectedTagSet(preferredSelectedId);
        tagState.loaded = true;
    } catch (error) {
        console.log("Error loading active tag sets:", error);
        toast.push(error.message, { classes: ['toast-danger'] });
    } finally {
        tagState.loading = false;
    }
}

// ── Data loading: all sets (TagsManager) ─────────────────────────────

/**
 * Load ALL tag sets (not just active) for the TagsManager CRUD view.
 * Also refreshes `tagState.activeSets`.
 */
export async function loadAllTagSets() {
    try {
        if (environment.online) {
            const { homeRef, userRef } = getFirebaseRefs();

            // Load all tag sets
            const querySnapshot = await getDocs(homeRef);
            const allSets = [];
            querySnapshot.forEach((tagSetDoc) => allSets.push(tagSetDoc.data()));
            tagState.allSets = allSets;

            // Load active set ids
            const userDoc = await getDoc(userRef);
            const userInfo = userDoc.data();
            tagState.activeSets = userInfo.tags || [];
        } else {
            tagState.allSets = JSON.parse(localStorage.getItem("cc-tag-sets")) || [];
            tagState.activeSets = JSON.parse(localStorage.getItem("cc-active-tag-sets")) || [];
        }
    } catch (error) {
        console.log("Error loading all tag sets:", error);
        toast.push(error.message, { classes: ['toast-danger'] });
    }
}

// ── CRUD operations (used by TagsManager) ────────────────────────────

export async function createTagSet(name) {
    const newTagSet = {
        id: uuidv4(),
        name: name || "New Tag Set",
        tags: []
    };

    try {
        if (environment.online) {
            const { homeRef } = getFirebaseRefs();
            await setDoc(doc(homeRef, newTagSet.id), newTagSet);
        }

        tagState.allSets = [...tagState.allSets, newTagSet];

        if (!environment.online) {
            localStorage.setItem("cc-tag-sets", JSON.stringify(tagState.allSets));
        }

        return newTagSet;
    } catch (error) {
        console.log("Error creating tag set:", error);
        toast.push(error.message, { classes: ['toast-danger'] });
        return null;
    }
}

export async function removeTagSet(id) {
    try {
        if (environment.online) {
            const { homeRef } = getFirebaseRefs();
            await deleteDoc(doc(homeRef, id));
        }

        tagState.allSets = tagState.allSets.filter((set) => set.id !== id);
        tagState.sets = tagState.sets.filter((set) => set.id !== id);

        // Also remove from active sets if present
        if (tagState.activeSets.includes(id)) {
            tagState.activeSets = tagState.activeSets.filter((s) => s !== id);
            await saveActiveSets();
        }

        if (!environment.online) {
            localStorage.setItem("cc-tag-sets", JSON.stringify(tagState.allSets));
        }
    } catch (error) {
        console.log("Error removing tag set:", error);
        toast.push(error.message, { classes: ['toast-danger'] });
    }
}

export async function saveTagSet(set) {
    if (!set) return;

    const tagSetObject = JSON.parse(JSON.stringify(set));

    try {
        if (environment.online) {
            const { homeRef } = getFirebaseRefs();
            await updateDoc(doc(homeRef, set.id), tagSetObject);
        }

        // Update in allSets
        const allIndex = tagState.allSets.findIndex((s) => s.id === set.id);
        if (allIndex > -1) {
            tagState.allSets[allIndex] = { ...set };
        }

        // Update in active sets if present
        const activeIndex = tagState.sets.findIndex((s) => s.id === set.id);
        if (activeIndex > -1) {
            tagState.sets[activeIndex] = { ...set };
        }

        if (!environment.online) {
            localStorage.setItem("cc-tag-sets", JSON.stringify(tagState.allSets));
        }
    } catch (error) {
        console.log("Error saving tag set:", error);
        toast.push(error.message, { classes: ['toast-danger'] });
        throw error;
    }
}

export async function toggleActiveSet(set) {
    if (tagState.activeSets.includes(set.id)) {
        tagState.activeSets = tagState.activeSets.filter((s) => s !== set.id);
        tagState.sets = tagState.sets.filter((s) => s.id !== set.id);
    } else {
        tagState.activeSets = [...tagState.activeSets, set.id];
        // Add to active sets for the panel
        const fullSet = tagState.allSets.find((s) => s.id === set.id);
        if (fullSet) {
            tagState.sets = [...tagState.sets, fullSet];
        }
    }

    await saveActiveSets();
}

export async function saveActiveSets() {
    try {
        if (environment.online) {
            const { userRef } = getFirebaseRefs();
            await updateDoc(userRef, { tags: tagState.activeSets });
        } else {
            localStorage.setItem("cc-active-tag-sets", JSON.stringify(tagState.activeSets));
        }
    } catch (error) {
        console.log("Error saving active sets:", error);
        toast.push(error.message, { classes: ['toast-danger'] });
    }
}

// ── Reset ────────────────────────────────────────────────────────────

export function resetTagState() {
    tagState.sets = [];
    tagState.activeSets = [];
    tagState.allSets = [];
    tagState.selectedId = null;
    tagState.loaded = false;
    tagState.loading = false;
}
